/**
 * Cost Breakdown Calculator
 *
 * Service to extract and categorize closing costs from order.accounting array.
 * Separates costs by role (buyer/seller) and charge type (earnest, commission, etc).
 * All monetary amounts are in cents to maintain precision.
 *
 * Functions:
 * - getCostBreakdownByRole: Extract buyer/seller costs by category
 */

import { NewOrderType } from 'types/order';
import { AccountingType } from 'v2-types/order';

/**
 * Cost category with label and amount
 */
export type CostCategory = {
  code: string;
  label: string;
  amount: number; // in cents
  category_key: string; // earnest, commission, title, escrow, taxes, other
};

/**
 * Cost breakdown for a role
 */
export type RoleCostBreakdown = {
  total_cents: number;
  categories: CostCategory[];
};

/**
 * Complete breakdown by role
 */
export type CostBreakdownResult = {
  buyer: RoleCostBreakdown;
  seller: RoleCostBreakdown;
};

/**
 * Map charge code to category
 *
 * Categories: earnest, commission, title, escrow, taxes, other
 * Used to group related charges for chart visualization.
 *
 * @param code - Charge code (e.g., 'earnest', 'commission', 'title_charge')
 * @returns Category key
 */
function categorizeByCode(code: string): string {
  if (!code) {
    return 'other';
  }

  const lowerCode = code.toLowerCase();

  if (lowerCode.includes('earnest')) {
    return 'earnest';
  }
  if (lowerCode.includes('commission') || lowerCode.includes('realtor')) {
    return 'commission';
  }
  if (lowerCode.includes('title') || lowerCode.includes('underwriting')) {
    return 'title';
  }
  if (lowerCode.includes('escrow') || lowerCode.includes('reserve')) {
    return 'escrow';
  }
  if (lowerCode.includes('tax') || lowerCode.includes('property tax') || lowerCode.includes('ad valorem')) {
    return 'taxes';
  }

  return 'other';
}

/**
 * Get human-readable label for category
 *
 * @param categoryKey - Category key (earnest, commission, etc)
 * @returns Human-readable label
 */
function getCategoryLabel(categoryKey: string): string {
  const labels: Record<string, string> = {
    earnest: 'Earnest Money',
    commission: 'Commissions',
    title: 'Title Charges',
    escrow: 'Escrow',
    taxes: 'Taxes & Fees',
    other: 'Other Charges',
  };
  return labels[categoryKey] || 'Other Charges';
}

/**
 * Extract and categorize closing costs by role
 *
 * Iterates through order.accounting array and organizes costs by:
 * 1. Role (buyer or seller, identified by order_kinds or kind field)
 * 2. Charge type (earnest, commission, title, escrow, taxes, other)
 *
 * Returns structured data ready for visualization components.
 *
 * @param order - The NewOrderType order object
 * @returns Object with buyer and seller cost breakdowns by category
 *
 * @example
 * // Returns { buyer: { total_cents: ..., categories: [...] }, seller: { ... } }
 * getCostBreakdownByRole(orderData);
 */
export function getCostBreakdownByRole(
  order: NewOrderType | null | undefined,
): CostBreakdownResult {
  // Handle null/undefined input
  if (!order || !order.accounting || !Array.isArray(order.accounting)) {
    return {
      buyer: { total_cents: 0, categories: [] },
      seller: { total_cents: 0, categories: [] },
    };
  }

  // Accumulate costs by role and category
  const buyerCostsByCategory: Record<string, { amount: number; label: string; code: string }> = {};
  const sellerCostsByCategory: Record<string, { amount: number; label: string; code: string }> = {};

  // Iterate through all accounting entries
  order.accounting.forEach((entry: AccountingType) => {
    if (!entry.involved_parties || !Array.isArray(entry.involved_parties)) {
      return;
    }

    const code = entry.code || 'unknown';
    const categoryKey = categorizeByCode(code);
    const categoryLabel = getCategoryLabel(categoryKey);

    // Process each party involved in this charge
    entry.involved_parties.forEach((party) => {
      // Identify if this party is buyer or seller
      const isOrderKindBuyer = party.order_kinds && party.order_kinds.some((k) => k.toLowerCase() === 'buyer');
      const isKindBuyer = party.kind === 'buyer';
      const isOrderKindSeller = party.order_kinds && party.order_kinds.some((k) => k.toLowerCase() === 'seller');
      const isKindSeller = party.kind === 'seller';

      // Buyer: include at_closing_amount only (money buyer pays at closing)
      if (isOrderKindBuyer || isKindBuyer) {
        const amount = party.at_closing_amount || 0;
        if (amount > 0) {
          const categoryId = categoryKey;
          if (!buyerCostsByCategory[categoryId]) {
            buyerCostsByCategory[categoryId] = {
              amount: 0,
              label: categoryLabel,
              code: categoryKey,
            };
          }
          buyerCostsByCategory[categoryId].amount += amount;
        }
      }

      // Seller: include at_closing_amount + before_closing_amount
      // (money seller pays/receives, both at and before closing)
      if (isOrderKindSeller || isKindSeller) {
        const amount = (party.at_closing_amount || 0) + (party.before_closing_amount || 0);
        if (amount !== 0) {
          const categoryId = categoryKey;
          if (!sellerCostsByCategory[categoryId]) {
            sellerCostsByCategory[categoryId] = {
              amount: 0,
              label: categoryLabel,
              code: categoryKey,
            };
          }
          sellerCostsByCategory[categoryId].amount += amount;
        }
      }
    });
  });

  // Build result structures
  const buyerCategories: CostCategory[] = Object.entries(buyerCostsByCategory).map(
    ([categoryId, data]) => ({
      code: data.code,
      label: data.label,
      amount: data.amount,
      category_key: categoryId,
    }),
  );

  const sellerCategories: CostCategory[] = Object.entries(sellerCostsByCategory).map(
    ([categoryId, data]) => ({
      code: data.code,
      label: data.label,
      amount: data.amount,
      category_key: categoryId,
    }),
  );

  // Calculate totals
  const buyerTotal = buyerCategories.reduce((sum, cat) => sum + cat.amount, 0);
  const sellerTotal = sellerCategories.reduce((sum, cat) => sum + cat.amount, 0);

  return {
    buyer: {
      total_cents: buyerTotal,
      categories: buyerCategories,
    },
    seller: {
      total_cents: sellerTotal,
      categories: sellerCategories,
    },
  };
}
